const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

export const isPreviewOrIframe = isPreviewHost || isInIframe;

/**
 * In preview/iframe contexts, unregister all PWA service workers and clear
 * Workbox caches so the editor always loads the latest build.
 * In preview/iframe contexts we unregister EVERY service worker, including
 * push-sw.js — push notifications are not needed in the editor and a stale
 * push worker can itself trap an old shell.
 */
export async function cleanupPWAInPreview(): Promise<boolean> {
  if (!isPreviewOrIframe) return false;
  if (!("serviceWorker" in navigator)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();
  let cleaned = false;

  for (const reg of registrations) {
    // In the preview, kill EVERY worker — nothing should be intercepting
    // requests to the editor iframe.
    try {
      reg.active?.postMessage({ type: "SKIP_WAITING" });
    } catch {
      /* noop */
    }
    await reg.unregister();
    cleaned = true;
  }

  // Clear every cache — including push-related ones in the preview.
  if ("caches" in window) {
    const keys = await caches.keys();
    for (const key of keys) {
      await caches.delete(key);
      cleaned = true;
    }
  }

  return cleaned;
}

/**
 * One-time service-worker reset.
 *
 * Unregisters every service worker **except** the dedicated push worker and
 * clears all Workbox / runtime caches.  This guarantees existing users break
 * out of the stale-version loop caused by the old root caching worker.
 *
 * Safe to run on every page load — if there is nothing to clean up it
 * returns `false` immediately.
 */
export async function nukeStaleWorkers(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();
  let cleaned = false;

  for (const reg of registrations) {
    const url = reg.active?.scriptURL ?? "";
    // Outside the preview we keep the dedicated push worker.
    // Inside the preview we kill everything.
    if (!isPreviewOrIframe && url.includes("push-sw.js")) continue;
    try {
      reg.active?.postMessage({ type: "SKIP_WAITING" });
    } catch {
      /* noop */
    }
    await reg.unregister();
    cleaned = true;
  }

  // Clear every cache created by the old Workbox service worker
  if ("caches" in window) {
    const keys = await caches.keys();
    for (const key of keys) {
      if (isPreviewOrIframe || !key.includes("push")) {
        await caches.delete(key);
        cleaned = true;
      }
    }
  }

  return cleaned;
}

/**
 * Returns true if a service worker is currently controlling this page.
 * If true after cleanup, the page must reload to break free of the SW.
 */
export function isControlledByServiceWorker(): boolean {
  return Boolean(
    typeof navigator !== "undefined" &&
      "serviceWorker" in navigator &&
      navigator.serviceWorker.controller,
  );
}
